import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import * as ccUtils from '../CC/utils';
import styles from './Record.module.css';
import ZoomedImage from './ZoomedImage';
import BasketManager from './BasketManager';
import ObjAssoc from './ObjAssoc';
import { Viewer360 } from '../Embed/Viewer360';

interface RecordProps {
  record: any;
}

interface Image {
  image: string;
  copyright: string;
  rank: string;
}

function replaceExtensionWithDzi(imageUrl: string): string {
  const urlWithoutExtension = imageUrl.substring(0, imageUrl.lastIndexOf('.'));
  return `${urlWithoutExtension}.dzi`;
}

function Record({ record }: RecordProps) {
  const { t, i18n } = useTranslation();
  const ctx = useCCContext();
  const filmStripRef = useRef<HTMLDivElement | null>(null);
  const siteObjectsCountRef = useRef({});
  const siteExhibitionsCountRef = useRef({});
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [noImageUrl, setNoImageUrl] = useState<string>(`${process.env.REACT_APP_IMAGE_BASE_URL}images/nondisponible.cci`); // Set default no image URL as string
  const [filmStripImages, setFilmStripImages] = useState<Image[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [siteRecordCounts, setSiteRecordCounts] = useState({ sitename: "", objects: 0, exhibitions: 0 });
  // const showViewer360 = record?.data?.record?.objectnumber === 'ULiege-AQMU-RE-014943';
  const showViewer360 = false;

  useEffect(() => {
    if (record) {
      const images = Array.isArray(record?.data?.record?.image) ? record?.data?.record?.image : [record?.data?.record?.image];
      const sortedImages = images.filter(Boolean).sort((a: Image, b: Image) => parseInt(a.rank) - parseInt(b.rank)); // Filter out null or undefined images
      setFilmStripImages(sortedImages);
      setCurrentIndex(0);

      if (filmStripRef.current) {
        filmStripRef.current.scrollLeft = 0;
      }
// JH 22.11.2024 - index now includes rank so imageUrl = {image, rank}
      const defaultImageUrl = sortedImages.length > 0 ? `${process.env.REACT_APP_DZI_BASE_URL}${replaceExtensionWithDzi(sortedImages[0].image)}` : noImageUrl;
      setImageUrl(defaultImageUrl);
    }
  }, [record]);

  useEffect(() => {
    const sitename = record?.data?.record?.sitename;
    let isCurrent = true;

    if (!sitename) {
      setSiteRecordCounts({ sitename: "", objects: 0, exhibitions: 0 });
      return () => {
        isCurrent = false;
      };
    }

    setSiteRecordCounts({ sitename, objects: 0, exhibitions: 0 });
    ccUtils.fetchSiteRecordCounts(sitename, ctx.user, siteObjectsCountRef, siteExhibitionsCountRef)
      .then((counts) => {
        if (isCurrent) {
          setSiteRecordCounts({ sitename, ...counts });
        }
      })
      .catch((error) => {
        if (isCurrent) {
          console.error("Error fetching site record counts:", error);
          setSiteRecordCounts({ sitename, objects: 0, exhibitions: 0 });
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [record?.data?.record?.sitename, ctx.user]);

  useEffect(() => {
    if (record?.data?.record?.title?.title) {
      document.title = `Les Collections du Musée de la Vie wallonne - ${record?.data?.record?.title?.title}`;
    }
  
    return () => {
      document.title = "Les Collections du Musée de la Vie wallonne";
      console.log('useEffect close', document.title);
    };
  }, [record]);


  if (!record) {
    return <div>No record selected.</div>;
  }

  function getHyperlinks(record: any): JSX.Element | null {
    const hyperlinks = record?.data?.record?.hyperlink;
  
    if (!hyperlinks) return null;
  
    const renderLink = (link: { objnrlink: string; hyperlink: string }) => {
      const linkText = link.objnrlink;
      return (
        <a 
          key={link.objnrlink} 
          href={link.hyperlink} 
          target="_blank" 
          rel="noopener noreferrer" 
          className={styles.tagValue}
        >
          {linkText}
        </a>
      );
    };
  
    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{t("record_hyperlink")} : </span>
        {Array.isArray(hyperlinks) 
          ? hyperlinks.map((link, index) => (
              <span key={link.objnrlink}>
                {renderLink(link)}
                {index < hyperlinks.length - 1 && ', '}
              </span>
            ))
          : renderLink(hyperlinks)}
      </div>
    );
  }

  function getMonumentWebsite(record: any): JSX.Element | null {
    const monumentWebsite = record?.data?.record?.monumentwebsite;
    if (!monumentWebsite) return null;

    const { hyperlinktextfr, hyperlinktexten, monumentwebsite: url } = monumentWebsite;
    const linkText = i18n.language === 'fr' ? hyperlinktextfr : hyperlinktexten;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{t("record_monument_website")} : </span>
        <a href={url} target="_blank" rel="noopener noreferrer" className={`${styles.tagValue} ${styles.tagValueHyperlink}`}>
          {linkText}
        </a>
      </div>
    );
  }
  
  function getThesaurusRowsStartLevel(
    record: any,
    label: string,
    CN: string,
    minLength: number,
    startLevel: number = 0
    ) {
      const thesTerms = record?.data?.record?.ThesTerm;
    
      if (!thesTerms) return null;
    
      const terms = Array.isArray(thesTerms)
        ? thesTerms.filter((term: any) => term.CN.startsWith(CN))
        : [thesTerms].filter((term: any) => term.CN.startsWith(CN));
    
      const rows = terms.map((term: any) => {
        let termElements: JSX.Element[] = [];
        let firstDone = false;
    
        const filteredParts = term.TermPathPart.slice(startLevel);
    
        filteredParts.forEach((part: any) => {
          if (part.CN && part.CN.length >= minLength) {
            termElements.push(
              <span key={part.CN}>
                {firstDone && (
                  <span className={styles.thesNodeSeperator}>&gt;</span>
                )}
                <span
                  className={styles.thesNode}
                  onClick={(e) =>
                    handleThesaurusNodeClick(e, part.CN, label, part.TermPathPart)
                  }
                >
                  {part.TermPathPart}
                </span>
              </span>
            );
            firstDone = true;
          }
        });
    
        return (
          <div className={styles.row} key={term.CN}>
            <span className={styles.taglabel}>{label} :{" "}</span> {/* Hier de spatie */}
            {termElements}
          </div>
        );
      });
    
      if (rows.length === 0) return null;
    
      return rows;
    } 

    function handleThesaurusNodeClick(event: React.MouseEvent, CN: string, label: string, term: string): void {
      event.stopPropagation();
      event.preventDefault();
      {/*ccUtils.customSearch(ctx, '/Record/mqb_thesterm/thesTerms/CN', '', '[' + CN + ']', label + ': ' + term);*/}
      //ccUtils.customSearch(ctx, '/record/ThesTerm/@CN', '', '[' + CN + ']', label + ': ' + term);
      ccUtils.customSearch(ctx, '/record/ThesTerm/CN', '', '[' + CN + ']', label + ': ' + term);
      //console.log(CN, term);
    }

  function getDefaultRowHyperlinkArray(
    record: any,
    label: string,
    fieldName: string,
    hyperlinkfield: string = "",
    defaultValue: string = ""
    ): JSX.Element | null {
      function getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      }
    
      console.log(fieldName);
      const fieldValue = getNestedValue(record?.data?.record, fieldName);
      console.log(fieldValue);
    
      const handleOnClick = (tag: string, value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        ccUtils.customSearch(ctx, '/record/' + tag, '', '' + value + '', label + ' : ' + value);
      };
    
      // Als fieldValue leeg is en er is geen defaultValue, return null
      if (!fieldValue && !defaultValue) return null;
    
      // Zorg ervoor dat fieldValue een array is
      const fieldValuesArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    
      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{label} : </span>
          <div>
            {fieldValuesArray.map((value, index) => (
              <div key={index}>
                <span
                  className={styles.authName}
                  onClick={(e) => handleOnClick(fieldName, value, e)}
                >
                  {value}
                </span>
              </div>
            ))}
            {!fieldValuesArray.length && defaultValue}
          </div>
        </div>
      );
  }
  
  function getThesaurusRowsGrouped(
    record: any,
    label: string,
    CN: string,
    minLength: number,
    startLevel: number = 0
    ) {
      const thesTerms = record?.data?.record?.ThesTerm;
    
      if (!thesTerms) return null;
    
      // Filter de termen op basis van CN
      const terms = Array.isArray(thesTerms)
        ? thesTerms.filter((term: any) => term.CN.startsWith(CN))
        : [thesTerms].filter((term: any) => term.CN.startsWith(CN));
    
      // Object om termen te groeperen per Type
      const groupedTerms: Record<string, JSX.Element[]> = {};
    
      terms.forEach((term: any) => {
        const filteredParts = term.TermPathPart
          .filter((part: any) => part.GuideTerm !== "1") // GuideTerm="1" weghalen
          .slice(startLevel);
    
        const termType = term.Type;
    
        if (filteredParts.length === 0) return;
    
        // De laatste term apart nemen
        const lastTerm = filteredParts[filteredParts.length - 1];
    
        // Hiërarchie opbouwen zonder de laatste term
        const hierarchyElements = filteredParts.slice(0, -1).map((part: any, index: number) => (
          <span key={part.CN}>
            {index > 0 && <span className={styles.thesNodeSeperator}>&gt;</span>}
            <span
              className={styles.thesNode}
              onClick={(e) =>
                handleThesaurusNodeClick(e, part.CN, label, part.TermPathPart)
              }
            >
              {part.TermPathPart}
            </span>
          </span>
        ));
    
        // Maak de laatste term ook klikbaar!
        const lastTermElement = (
          <span
            key={lastTerm.CN}
            className={styles.thesNode}
            onClick={(e) =>
              handleThesaurusNodeClick(e, lastTerm.CN, label, lastTerm.TermPathPart)
            }
          >
            {lastTerm.TermPathPart}
          </span>
        );
    
        // Voeg laatste term en hiërarchie samen, met hiërarchie tussen haakjes
        const termElement = (
          <div key={term.CN} className={styles.thesTermRow}>
            {lastTermElement} {/* Klikbare hoofdterm */}
            {hierarchyElements.length > 0 && (
              <>
                &nbsp;(<span className={styles.thesHierarchy}>{hierarchyElements}</span>)
              </>
            )}
          </div>
        );
    
        // Voeg termen toe aan de juiste Type-groep
        if (!groupedTerms[termType]) {
          groupedTerms[termType] = [];
        }
        groupedTerms[termType].push(termElement);
      });
    
      // Maak JSX-output aan met Type slechts één keer
      const allTermElements = Object.entries(groupedTerms).map(([type, elements]) => (
        <div key={type} className={styles.thesTypeGroup}>
          <strong className={styles.rowType}>{type} :</strong>&nbsp;
          <div className={styles.thesTermsContainer}>{elements}</div>
        </div>
      ));
    
      // Toon niets als er geen termen zijn
      if (allTermElements.length === 0) return null;
    
      return <div className={styles.row}>{allTermElements}</div>;
  }
  
  
  const handleScrollLeft = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: -300,
      behavior: 'smooth'
    });
  };

  const handleFilmStripImageClick = (image: Image, idx: number) => {
    const newImageFilename = replaceExtensionWithDzi(image.image);
    const newImageUrl = `${process.env.REACT_APP_DZI_BASE_URL}${newImageFilename}`;
    setImageUrl(newImageUrl);
    setCurrentIndex(idx);
  };

  const handleScrollRight = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: 300,
      behavior: 'smooth'
    });
  };

  function getDefaultRow(record: any, label: string, fieldName: string, defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }
  
    let fieldValue = getNestedValue(record?.data?.record, fieldName);
  
    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
      fieldValue = JSON.stringify(fieldValue);
    }
  
    if (fieldName === "classement") {
      const date = record?.data?.record?.classementdate;
      const formattedDate = typeof date === "string"
        ? date.trim().replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3-$2-$1')
        : date;
      if (formattedDate) {
        fieldValue = [fieldValue, `(${formattedDate})`].filter(Boolean).join(' ');
      }
    }

    if (!fieldValue && !defaultValue) return null;
  
    // the <span id={fieldName} adds the fieldname as id value, so the field value can be used for pdf creation
    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        <span
          id={fieldName}
          className={styles.tagValue}
          dangerouslySetInnerHTML={{ __html: fieldValue ?? defaultValue }}
        ></span>
      </div>
    );
  }

  function getDefaultRowExtLink(record: any, label: string, fieldName: string, defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }
  
    let fieldValue = getNestedValue(record?.data?.record, fieldName);
  
    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(' - ');
    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
      fieldValue = JSON.stringify(fieldValue);
    }
  
    if (!fieldValue && !defaultValue) return null;
  
    if (typeof fieldValue === 'string') {
      const urlRegex = /((https?:\/\/)?(www\.)?[^\s]+\.[^\s]{2,})/gi;
      const linkClass = styles.link;
    
      fieldValue = fieldValue.replace(urlRegex, (url) => {
        let fullUrl = url;
        if (!/^https?:\/\//i.test(url)) {
          fullUrl = 'http://' + url;
        }
        return `<a href="${fullUrl}" class="${linkClass}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
      }
    
      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{label} : </span>
          <span
            className={styles.tagValue}
            dangerouslySetInnerHTML={{ __html: fieldValue ?? defaultValue }}
          ></span>
        </div>
      );
    }  

  function getDefaultRowNoFieldNameOrange(
    record: any, 
    label: string, 
    fieldName: string, 
    defaultValue: string = ""
    ): JSX.Element | null {
      function getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      }
    
      let fieldValue = getNestedValue(record?.data?.record, fieldName);
    
      if (Array.isArray(fieldValue)) {
        fieldValue = fieldValue.join(', ');
      } else if (typeof fieldValue === 'object' && fieldValue !== null) {
        fieldValue = JSON.stringify(fieldValue);
      }
    
      if (!fieldValue && !defaultValue) return null;
    
      return (
        <span
          className={styles.RowNoFieldNameOrange}
          dangerouslySetInnerHTML={{ __html: (fieldValue ?? defaultValue) + "&nbsp;&nbsp;&nbsp;" }}
        ></span>
      );
  }  

  function getDefaultRowNoFieldName(
    record: any, 
    label: string, 
    fieldName: string, 
    defaultValue: string = ""
    ): JSX.Element | null {
      function getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      }
    
      let fieldValue = getNestedValue(record?.data?.record, fieldName);
    
      if (Array.isArray(fieldValue)) {
        fieldValue = fieldValue.join(', ');
      } else if (typeof fieldValue === 'object' && fieldValue !== null) {
        fieldValue = JSON.stringify(fieldValue);
      }
    
      if (!fieldValue && !defaultValue) return null;
    
      return (
        <span
          className={styles.RowNoFieldName}
          dangerouslySetInnerHTML={{ __html: (fieldValue ?? defaultValue) + "&nbsp;&nbsp;&nbsp;" }}
        ></span>
      );
  }
  
  function getDefaultRowLinkSiteObjects(record: any, label: string, fieldName: string, defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }
  
    let fieldValue = getNestedValue(record?.data?.record, fieldName);
  
    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
      fieldValue = JSON.stringify(fieldValue);
    }
  
    if (!fieldValue && !defaultValue) return null;
  
    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} </span>
        <span
          className={styles.tagValue}
          dangerouslySetInnerHTML={{
            __html: `<strong><u>${fieldValue ?? defaultValue}</u></strong>`,
          }}
        ></span>
      </div>
    );
  }

  function getPdfEmbed(record: any): JSX.Element | null {
    const pdfs = record?.data?.record?.pdf;
  
    if (!pdfs || (Array.isArray(pdfs) && pdfs.length === 0)) return null;
  
    // Zorg dat we altijd met een array werken
    const pdfArray = Array.isArray(pdfs) ? pdfs : [pdfs];
  
    return (
      <>
        {pdfArray.map((pdf: string, index: number) => {
          const pdfUrl = `https://dev.cithosting.nl/mdlvw_pdf/${encodeURIComponent(pdf)}`;
  
          return (
            <div key={index} style={{ margin: "20px 0" }}>
              {/* Embedded PDF */}
              <embed
                src={pdfUrl}
                width="600"
                height="800"
                type="application/pdf"
              />
  
              {/* Hyperlink naar de PDF */}
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  marginTop: "10px",
                  color: "#007bff",
                  textDecoration: "underline"
                }}
              >
                {t("Open PDF in a new tab")}
              </a>
            </div>
          );
        })}
      </>
    );
  }
  
  
  function getAudioElements(record: any): JSX.Element | null {
    const audioBaseUrl = "https://dev.cithosting.nl/mdlvw_files/";
    const audioKeys = ["audio1", "audio2", "audio3"];
  
    const audioElements = audioKeys
      .map((key) => record?.data?.record?.[key]) // Haal de waarden van audio1, audio2, audio3 op
      .filter((audio) => audio) // Alleen niet-lege waarden
      .map((audio, index) => {
        // Controleer of de audio al correct geëncodeerd is
        const cleanedAudio = audio.replace(/%2523/g, "%23"); // Herstel dubbele encoding indien aanwezig
  
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <audio controls>
              <source src={`${audioBaseUrl}${cleanedAudio}`} type="audio/wav" />
              {t("Your browser does not support the audio element.")}
            </audio>
          </div>
        );
      });
  
    if (audioElements.length === 0) return null; // Geen audio-elementen aanwezig
  
    return <>{audioElements}</>;
  }
  
  function getDefaultRowHyperlink(record: any, label: string, fieldName: string, hyperlinkfield: string = "", defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    console.log(fieldName);
    let fieldValue = getNestedValue(record?.data?.record, fieldName);
    console.log(fieldValue);

    const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      ccUtils.customSearch(ctx, '/record/' + hyperlinkfield, '', '' + value + '', label + ' : ' + value);
    };

    if (!fieldValue && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        {fieldValue
          ? <span className={styles.authName} onClick={(e) => handleOnClick(label, fieldValue, e)}>{fieldValue}</span>
          : defaultValue}
      </div>
    );
  }
  
  function getPersonnes(record: any, tag: keyof typeof record.data.record) {
    const elements = record?.data?.record?.[tag];
    if (!elements) return null;

    // Convert to array if not already
    const elementArray = Array.isArray(elements) ? elements : [elements];

    // Sorting logic (if required)
    elementArray.sort((a, b) => {
        const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
        const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
        return keyA.localeCompare(keyB);
    });

    const handleOnClick = (e: React.MouseEvent, name: string) => {
        e.preventDefault();
        e.stopPropagation();
        ccUtils.customSearch(ctx, '/record/creatorname,/record/conxother', '', '[' + name + ']', t('Constituent') + ' : ' + name);
    };

    const rows = elementArray.map((element: any, index) => {
        const { Role, [tag]: tagValue, DisplayDate } = element;
        return (
            <div className={styles.row} key={index}>
                <span className={styles.taglabel}>{Role} : </span>
                <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
                {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
            </div>
        );
    });

    if (rows.length === 0) return null;

    return <>
        <div>
            {/* {t("record_personnes_institutions")} */} 
        </div>
        <div>
            {rows}
        </div>
    </>
}

function getTitles(record: any) {
  // Haal de titels op
  const titles = record?.data?.record?.titlealt;
  if (!titles) return null;

  // Converteer naar een array als het geen array is
  const titleArray = Array.isArray(titles) ? titles : [titles];

  // Sorteer op displayorder
  titleArray.sort((a, b) => a.displayorder - b.displayorder);

  // Maak de JSX-elementen aan
  const rows = titleArray.map((title: any, index: number) => (
    <div className={styles.row} key={index}>
      <span className={styles.taglabel}>{title.titletype} :</span>
      <span className={styles.tagValue}>&nbsp;{title.titlealt}</span>
    </div>
  ));

  return <>{rows}</>;
}

function getconXothers(record: any, tag: keyof typeof record.data.record) {
  const elements = record?.data?.record?.[tag];
  if (!elements) return null;

  // Convert to array if not already
  const elementArray = Array.isArray(elements) ? elements : [elements];

  // Sorting logic (if required)
  elementArray.sort((a, b) => {
      const keyA = a.roletypeid + ',' + a.displayorder;
      const keyB = b.roletypeid + ',' + b.displayorder;
      return keyA.localeCompare(keyB);
  });

  const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/record/creatorname,/record/conxother', '', '[' + name + ']', t('Constituent') + ' : ' + name);
  };

  const rows = elementArray.map((element: any, index) => {
      const { role, [tag]: tagValue, DisplayDate } = element;
      return (
          <div className={styles.row} key={index}>
              <span className={styles.taglabel}>{role} : </span>
              <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
              {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
          </div>
      );
  });

  if (rows.length === 0) return null;

  return <>
      <div>
          {/* {t("record_personnes_institutions")} */} 
      </div>
      <div>
          {rows}
      </div>
  </>
}

  try {
    return (
      <div id="pdf-container" className={styles.Record}>
        <div className={`pdfOnly`} style={{ marginBottom: '20px' }}>
        <div className={styles.title}>
            {getDefaultRowNoFieldName(record, t("record_title"), "title.title")}
          </div>   
        </div>
        <div className={styles.ccListviewRecordImage + " pdfCenter"}>
          <div className={styles.imgItemWrapper}>
            <span className={styles.imgItem + " " + styles.imgItemFirst}>
              <div className={styles.ZoomedImage + " pdfNoBorder"}>
                {!imageUrl && noImageUrl && <img src={noImageUrl} alt="No image available" />}
                {imageUrl && <ZoomedImage key={imageUrl} dzi={imageUrl} downloadAllowed={true} images={filmStripImages.map(img => ({ imagesmore: img.image, copyright: img.copyright, rank: img.rank }))} initialIndex={currentIndex} />}
              </div>
            </span>
          </div>
          {filmStripImages.length > 1 && (
            <div className={styles.filmStripContainer + " screenOnly"}>
              <div className={styles.buttonContainer}>
                <GenericIconButton icon='light/angle-left.svg' width='22px' height='20px' onClick={handleScrollLeft} />
              </div>
              <div className={styles.filmStrip} ref={filmStripRef}>
                {filmStripImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={`${process.env.REACT_APP_IMAGE_BASE_URL}${img.image}&height=100&bg=ffffff&cache=yes`}
                    alt={`Image rank ${img.rank}`}
                    className={styles.filmStripImage}
                    onClick={() => handleFilmStripImageClick(img, idx)}
                    />
                ))}
              </div>
              <div className={styles.buttonContainer}>
                <GenericIconButton icon='light/angle-right.svg' width='22px' height='20px' onClick={handleScrollRight} />
              </div>
            </div>
          )}
        </div>

        <div className={styles.ccListviewRecordText + " pdfUnder"}>
          <div className={styles.group}>
            {getDefaultRowNoFieldNameOrange(record, t("record_objectnumber"), "objectnumber")}
            {getDefaultRowNoFieldNameOrange(record, t("record_institution"), "institutions")}
            {getDefaultRowNoFieldNameOrange(record, t("record_objectstatus"), "objectstatus")}
            {getDefaultRowNoFieldNameOrange(record, t("record_displayname"), "displayname")}
            {getDefaultRowNoFieldNameOrange(record, t("record_exhtitle"), "exhtitle")}
            {getDefaultRowNoFieldNameOrange(record, t("record_displayname"), "displaydate")}
            {["expose"].reduce((acc, part) => acc && acc[part], record?.data?.record) === "Oui" && (
              <span className={styles.RowNoFieldNameOrange}>Exposé</span>
            )}
            {getDefaultRow(record, t("record_exhtitle"), "exhtitle")}
            {getDefaultRow(record, t("record_exhsubtitle"), "exhsubtitle")}
          </div>

          <div className={styles.group}>
            {getDefaultRow(record, t("record_classification"), "classification")}
          </div>

          <div className={styles.group}>
            {getThesaurusRowsStartLevel(record, t("Fonds"), "MVW.AAX", 3, 2)}
          </div>

          <div className={styles.group}>
            {getDefaultRow(record, t("record_objectname"), "objectname")}
            {getTitles(record)}
          </div>

          <div className={styles.group}>
            {record?.data?.record?.recordtype !== "exhibitions" && getPersonnes(record, "creatorname")}
            {getDefaultRowHyperlink(record, t("record_auteur"), "auteur.auteur", "auteur")}                              
            {getDefaultRow(record, t("record_remarqueattri"), "remarqueattri")}
          </div>

          {/*}Specific for Site records*/}
          {getDefaultRowNoFieldNameOrange(record, t("record_sitename"), "sitename")}
          {record?.data?.record?.sitename && siteRecordCounts.sitename === record.data.record.sitename && siteRecordCounts.objects > 0 && (
            <div className={styles.buttonLinkWrapper}> {/* Zorgt voor centreren en witruimte */}
              <a
                href={`?state=${encodeURIComponent(
                JSON.stringify({
                Searches: [
                {
                identifier: "simpleSearch",
                field: "All search fields",
                },
                ],
                matchAllCriteria: true,
                facets: {
                institution: [record.data.record.sitename], // sitename wordt in JSON.stringify verwerkt
                recordtype: ["Objets"],
                },
                })
                )}`}
                target="_top" // Opent de link in hetzelfde tabblad
                rel="noopener noreferrer" // Verhoogt veiligheid bij externe links
                className={styles.buttonLink} // Nieuwe klasse voor knopstijl
              >
                {t("record_sitenameobjects")}
              </a>
            </div>
          )}

          {record?.data?.record?.sitename && siteRecordCounts.sitename === record.data.record.sitename && siteRecordCounts.exhibitions > 0 && (
            <div className={styles.buttonLinkWrapper}>
              <a
                href={`?state=${encodeURIComponent(
                  JSON.stringify({
                    Searches: [],
                    matchAllCriteria: true,
                    customSearchCriteria: [
                      {
                        field: "/record/institution",
                        fieldLabel: "Institution",
                        searchValues: [record.data.record.sitename],
                        description: record.data.record.sitename,
                      },
                      {
                        field: "/record/recordtypeshow",
                        fieldLabel: "Type",
                        searchValues: ["Exposition"],
                        description: "Exposition",
                      },
                    ],
                  })
                )}`}
                target="_top"
                rel="noopener noreferrer"
                className={styles.buttonLink}
              >
                {t("record_sitenameexhibitions")}
              </a>
            </div>
          )}

          {/*}Specific for P&I records*/}
          {/*getDefaultRow(record, t("record_institution"), "institution")*/}
          <div className={styles.group}>
            {getDefaultRow(record, t("record_lastname"), "lastname")}
            {getDefaultRow(record, t("record_firstname"), "firstname")}
            {getDefaultRow(record, t("record_nametitle"), "nametitle")}
            {getDefaultRowNoFieldName(record, t("record_altname_typeplusname"), "altname.typeplusname")}
          </div>
          <div className={styles.group}>
            {getDefaultRow(record, t("record_nationality"), "nationality")}
            {getDefaultRow(record, t("record_biography"), "biography")}
          </div>
          <div className={styles.group}>
            {getDefaultRow(record, t("record_begindate"), "begindate")}
            {getDefaultRow(record, t("record_enddate"), "enddate")}
          </div>

          {/*}Specific for Exh records*/}                            
          {/*getDefaultRow(record, t("record_displaydate"), "displaydate")*/}
          {/*record?.data?.record?.recordtype === "exhibitions" && getPersonnes(record, "creatorname")*/}
          {getDefaultRow(record, t("record_citation"), "citation")}
          {record?.data?.record?.recordtype === "exhibitions" && getPersonnes(record, "creatorname")}

          {/*}Specific for HistEvent records - worden momenteel niet getoond*/}
          {getDefaultRow(record, t("record_eventname"), "eventname")}

          {/* Extra velden record view */}
        <div className={styles.group}>
          {["recordtype"].reduce((acc, part) => acc && acc[part], record?.data?.record) !== "objects"
          ? getDefaultRowNoFieldName(record, t("record_description"), "description")
          : getDefaultRow(record, t("record_description"), "description")}
        </div>

        <div className={styles.group}>
          {getDefaultRow(record, t("record_famille"), "famille")}
          {getDefaultRow(record, t("record_genre"), "genre")}
          {getDefaultRow(record, t("record_espece"), "espece")}
          {getDefaultRow(record, t("record_parrain"), "parrain")}
          {getDefaultRow(record, t("record_thesgroupesbio"), "thesgroupesbio")}
          {getDefaultRow(record, t("record_thesstatuttaxo"), "thesstatuttaxo")}
        </div>

        <div className={styles.group}>
          {getDefaultRow(record, t("record_medium"), "medium")}
          {getDefaultRow(record, t("record_dimensions"), "dimensions")}
        </div>

        <div className={styles.group}>
          {getThesaurusRowsGrouped(record, t("Lieu"), "MVW.AAL", 3, 2)}
          {getDefaultRow(record, t("record_theslieuevenement"), "theslieuevenement")}
          {getDefaultRow(record, t("record_theslieuinterview"), "theslieuinterview")}
          {getDefaultRow(record, t("record_relatedworks"), "relatedworks")}
        </div>

        <div className={styles.group}>
          {getDefaultRow(record, t("record_dated"), "dated")}
          {getDefaultRow(record, t("record_dateremarks"), "dateremarks")}
          {getDefaultRow(record, t("record_period"), "thesperiod")}
          {getDefaultRow(record, t("record_thestempsgeo"), "thestempsgeo")}
          {getDefaultRow(record, t("record_altdates"), "altdates")}
          {getDefaultRow(record, t("record_culture"), "culture")}
          {getDefaultRow(record, t("record_school"), "school")}
        </div>

        <div className={styles.group}>
          {getconXothers(record, 'conxother')}
          {getDefaultRow(record, t("record_accessiondate"), "accessiondate")}
          {getDefaultRow(record, t("record_accessionmethod"), "accessionmethod")}
        </div>

        <div className={styles.group}>
          {getDefaultRow(record, t("record_classement"), "classement")}
        </div>

        {getDefaultRowNoFieldName(record, t("record_youtube"), "youtube")}<br  />

        {getPdfEmbed(record)}
        {getAudioElements(record)}

        {showViewer360 && (
          <div className="screenOnly">
            <Viewer360 />
          </div>
        )}

        <span className={styles.tagValue + "  screenOnly"}>
          <strong>{t("Lien permanent")} : </strong>
          {`commoncollections.be/?id=${record?.data?.record?.uniqid || "Pas d'objet"}`}
          <button
            onClick={() =>
            navigator.clipboard.writeText(
            `commoncollections.be/?id=${record?.data?.record?.uniqid || "Pas d'objet"}`
            )
            }
            style={{
            marginLeft: "10px",
            padding: "5px 10px",
            fontSize: "14px",
            cursor: "pointer",
            }}
            >
            Copie
          </button>
        </span>

        <div className={styles.group}>
          {record?.data?.record?.recordtype === "exhibitions" && (
            <div className={styles.group}>
              <br />{getDefaultRowExtLink(record, t("record_lienexterne"), "lienexterne")}
            </div>
          )}
        </div>
        <div className={`screenOnly`}>
          <ObjAssoc record={record}></ObjAssoc>
        </div>   
        
        {/* Knop voor meer informatie - alleen voor Objects*/}
        {(
          ["recordtype"].reduce((acc, part) => acc && acc[part], record?.data?.record) === "objects" 
        ) && (
          <>
            <br />
            <div className={`screenOnly`}>
              <button
                onClick={() => setShowMoreInfo((prev) => !prev)}
                style={{
                marginTop: "20px",
                padding: "10px 15px",
                fontSize: "14px",
                cursor: "pointer",
                backgroundColor: "#25344f",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                }}
              >
                {t(showMoreInfo ? "Less information" : "More information")}
              </button>
              <br />
              <br />
            </div>
            <div className="record-container pdfOnly">
              <div className={styles.group}>
                {getDefaultRow(record, t("record_objectlevel"), "objectlevel")}
                {getDefaultRow(record, t("record_objectcount"), "objectcount")}
              </div>

              <div className={styles.group}>
                {getDefaultRow(record, t("record_state"), "state")}
                {getDefaultRow(record, t("record_inscribed"), "inscribed")}
                {getDefaultRow(record, t("record_papersupport"), "papersupport")}
                {getDefaultRow(record, t("record_markings"), "markings")}
                {getDefaultRow(record, t("record_signed"), "signed")}
                {getDefaultRow(record, t("record_portfolio"), "portfolio")}
                {getDefaultRow(record, t("record_edition"), "edition")}
              </div>

              <div className={styles.group}>
                {getDefaultRow(record, t("record_thesicono"), "thesicono")}
              </div>

              <div className={styles.group}>
                {getDefaultRow(record, t("record_provenance"), "provenance")}
              </div>

              <div className={styles.group}>
                {getDefaultRow(record, t("record_notes"), "notesobj")}
              </div>

              <div className={styles.group}>
                {getDefaultRow(record, t("record_curatorialremarks"), "curatorialremarks")}
              </div>

              <div className={styles.group}>
                {getDefaultRow(record, t("record_pubreferences"), "pubreferences")}
              </div>

              {record?.data?.record?.recordtype !== "exhibitions" && (
                <div className={styles.group}>
                  {getDefaultRowExtLink(record, t("record_lienexterne"), "lienexterne")}
                </div>
              )}

              <div className={styles.group}>
                {getThesaurusRowsGrouped(record, t("Theme"), "MVW.AAJ", 3, 2)}
              </div>

              <div className={styles.group}> 
                {getDefaultRow(record, t("record_dialecte"), "dialecte")}
              </div>

              <div className={styles.group}> 
                {getDefaultRow(record, t("record_metier"), "metier")}
                {getDefaultRow(record, t("record_collection"), "webcollection")}
              </div>
            </div>
            
            {/* Extra velden die worden verborgen of weergegeven */}
            {showMoreInfo && (
              <>
                <div className="record-container">
                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_objectlevel"), "objectlevel")}
                    {getDefaultRow(record, t("record_objectcount"), "objectcount")}
                  </div>

                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_state"), "state")}
                    {getDefaultRow(record, t("record_inscribed"), "inscribed")}
                    {getDefaultRow(record, t("record_papersupport"), "papersupport")}
                    {getDefaultRow(record, t("record_markings"), "markings")}
                    {getDefaultRow(record, t("record_signed"), "signed")}
                    {getDefaultRow(record, t("record_portfolio"), "portfolio")}
                    {getDefaultRow(record, t("record_edition"), "edition")}
                  </div>

                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_thesicono"), "thesicono")}
                  </div>

                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_provenance"), "provenance")}
                  </div>

                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_notes"), "notesobj")}
                  </div>

                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_curatorialremarks"), "curatorialremarks")}
                  </div>

                  <div className={styles.group}>
                    {getDefaultRow(record, t("record_pubreferences"), "pubreferences")}
                  </div>

                  {record?.data?.record?.recordtype !== "exhibitions" && (
                    <div className={styles.group}>
                      {getDefaultRowExtLink(record, t("record_lienexterne"), "lienexterne")}
                    </div>
                  )}

                  <div className={styles.group}>
                    {getThesaurusRowsGrouped(record, t("Theme"), "MVW.AAJ", 3, 2)}
                  </div>

                  <div className={styles.group}> 
                    {getDefaultRow(record, t("record_dialecte"), "dialecte")}
                  </div>

                  <div className={styles.group}> 
                    {getDefaultRow(record, t("record_metier"), "metier")}
                    {getDefaultRow(record, t("record_collection"), "webcollection")}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {/*Hier weer verder met de altijd getoonde data*/}                        

      <div>
        <div className={styles.basketManagerContainer + " screenOnly"}>
          <BasketManager
          objectId={record?.data?.record?.ccobjectid}
          />
        </div>
      </div>
      </div>
    );
  } catch (e) {
    setError(e as any);
    return <div>Error: {(e as any).message}</div>;
  }
}

export default Record;
